/**
 * Site Configuration
 * Minimal config - only URL and affiliate link
 * All text content is in locale files
 */

import { DOMAIN_NAME } from './domain'
import { NICHE } from './niche'
import { LANGUAGE } from './language'
import { AFFILIATE, AFFILIATE_LINK, AFFILIATE_LOGO_FILE } from './affiliate'
import { SITE_NAME } from './siteName'

/**
 * Get city slug prefix based on language and niche
 * Format: {language-keyword}-{niche}-{city}
 * Examples:
 * - French: rencontres-gay-lille (FR, BE-FR, CH-FR)
 * - German: treffen-trans-berlin (DE, AT, CH-DE when NICHE=trans)
 * - English: meet-gay-london (US, UK, CA, AU, NZ, IE, etc.)
 * - Polish: spotkania-trans-warsaw (PL when NICHE=trans)
 * - Dutch: ontmoetingen-gay-amsterdam (NL, BE-NL)
 * - Italian: incontri-gay-rome (IT, CH-IT)
 * - Spanish: encuentros-gay-madrid (ES)
 * - Czech: setkani-trans-prague (CZ when NICHE=trans)
 * - Danish: moder-gay-copenhagen (DK)
 * - Finnish: tapaamiset-gay-helsinki (FI)
 * - Norwegian: moter-gay-oslo (NO)
 * - Swedish: traffar-gay-stockholm (SE)
 * - Slovak: stretnutia-trans-bratislava (SK when NICHE=trans)
 * - Hebrew: pgishot-gay-telaviv (IL)
 */
const getCitySlugPrefix = () => {
  const nicheLower = NICHE.toLowerCase()
  
  // Language-specific keywords for "meet/rencontre/treffen/spotkania"
  // Note: The niche will be added separately, so don't include it in the keyword
  // English-speaking countries (US, UK, CA, AU, NZ, IE, etc.) use 'en' only
  // Country mappings:
  // - AT, DE, CH (German): de
  // - AU, CA, GB, IE, NZ, US (English): en
  // - BE, CH (French), FR: fr
  // - BE, NL (Dutch): nl
  // - CH, IT: it
  // - CZ: cs
  // - DK: da
  // - ES: es
  // - FI: fi
  // - IL: he
  // - NO: no
  // - PL: pl
  // - SE: sv
  // - SK: sk
  const languageKeywords = {
    fr: 'rencontres',      // France, Belgium (French), Switzerland (French)
    de: 'treffen',         // Germany, Austria, Switzerland (German)
    en: 'meet',            // US, UK, Canada, Australia, New Zealand, Ireland
    pl: 'spotkania',       // Poland
    nl: 'ontmoetingen',    // Netherlands, Belgium (Dutch)
    it: 'incontri',        // Italy, Switzerland (Italian)
    es: 'encuentros',      // Spain
    cs: 'setkani',         // Czech Republic
    da: 'moder',           // Denmark
    fi: 'tapaamiset',      // Finland
    no: 'moter',           // Norway
    sv: 'traffar',         // Sweden
    sk: 'stretnutia',      // Slovakia
    he: 'pgishot'          // Israel (transliterated Hebrew)
  }
  
  const keyword = languageKeywords[LANGUAGE] || 'meet'
  return `${keyword}-${nicheLower}-`
}

export const siteConfig = {
  // Base URL - read from domain config
  baseUrl: `https://${DOMAIN_NAME}`,
  
  // City slug prefix - language-based: rencontres-gay-, treffen-trans-, meet-gay-, spotkania-trans-, etc.
  citySlugPrefix: getCitySlugPrefix(),
  
  // Affiliate/Monetization
  affiliate: {
    name: AFFILIATE || SITE_NAME,
    link: AFFILIATE_LINK
  },
  
  // Article Generation Configuration
  articleGeneration: {
    // Language for generated articles ('de' for German, 'en' for English)
    language: LANGUAGE,
    
    // Theme/topic for articles
    theme: `dating ${NICHE} `,
    
    // Page types to generate (slugs are in locale files)
    pageTypes: {
      // Global hub page (slug from locale: pages.slugs.globalHub)
      globalHub: {
        enabled: true,
        localeKey: 'pages.slugs.globalHub',
        priority: 1.0
      },
      // Segment pages (slugs from locale: pages.slugs.*)
      segmentPages: {
        enabled: true,
        pages: [
          { localeKey: 'pages.slugs.apps', type: 'apps', priority: 0.9 },
          { localeKey: 'pages.slugs.bestSites', type: 'comparison', priority: 0.9 },
          { localeKey: 'pages.slugs.hookups', type: 'hookups', priority: 0.9 },
          { localeKey: 'pages.slugs.sexDating', type: 'adult', priority: 0.9 },
          { localeKey: 'pages.slugs.howToDate', type: 'advice', priority: 0.8 },
          { localeKey: 'pages.slugs.tips', type: 'tips', priority: 0.8 },
          { localeKey: 'pages.slugs.topApps', type: 'ranking', priority: 0.9 }
        ]
      },
      // City pages (lightweight, 50-100 words)
      cityPages: {
        enabled: true,
        wordCount: 100,
        priority: 0.7
      }
    },
    
    // Output directory for generated articles
    outputDir: 'src/data/articles'
  }
}

/**
 * Get cloaked affiliate link (clean URL that redirects to affiliate)
 * @returns {string} Cloaked affiliate link URL
 */
export const getAffiliateLink = () => {
  return `${siteConfig.baseUrl}/anmelden`
}

/**
 * Get direct affiliate link (for redirects)
 * @returns {string} Direct affiliate link URL
 */
export const getDirectAffiliateLink = () => {
  return siteConfig.affiliate.link
}

/**
 * Brand name to show in UI (affiliate name)
 * @returns {string}
 */
export const getAffiliateName = () => {
  return siteConfig.affiliate.name || SITE_NAME
}

/**
 * Logo path based on affiliate name.
 * Expects file at: public/images/logos/{AFFILIATE}.png
 * @returns {string}
 */
export const getAffiliateLogoPath = () => {
  const file = AFFILIATE_LOGO_FILE || `${getAffiliateName()}.png`
  return `/images/logos/${file}`
}

/**
 * Profile image paths based on niche folder.
 * Expects files at: public/images/{nicheLower}/{1..N}.jpg
 * @param {number} count
 * @returns {string[]}
 */
export const getProfileImagePaths = (count = 8) => {
  const nicheLower = (NICHE || '').toLowerCase()
  return Array.from({ length: count }, (_, i) => `/images/${nicheLower}/${i + 1}.jpg`)
}

/**
 * Get city slug with prefix for URLs
 * @param {string} baseSlug - The base city slug (e.g., "berlin")
 * @returns {string} Full slug with prefix (e.g., "treffen-trans-berlin" when NICHE=trans)
 */
export const getCitySlugWithPrefix = (baseSlug) => {
  return `${siteConfig.citySlugPrefix}${baseSlug}`
}

/**
 * Extract base city slug from URL slug (removes prefix)
 * @param {string} urlSlug - The slug from URL (e.g., "treffen-trans-berlin" when NICHE=trans)
 * @returns {string} Base slug (e.g., "berlin")
 */
export const getBaseSlugFromUrl = (urlSlug) => {
  if (!urlSlug) return null
  if (urlSlug.startsWith(siteConfig.citySlugPrefix)) {
    return urlSlug.substring(siteConfig.citySlugPrefix.length)
  }
  // Fallback: if no prefix, assume it's already a base slug (for backwards compatibility)
  return urlSlug
}

/**
 * Generate sitemap URL for a city
 * @param {string} citySlug - The base city slug (e.g., "berlin")
 * @returns {string} Full URL with prefix
 */
export const getCityUrl = (citySlug) => {
  const fullSlug = getCitySlugWithPrefix(citySlug)
  return `${siteConfig.baseUrl}/${fullSlug}`
}

/**
 * Generate sitemap entry for a city
 * @param {Object} city - City object with Slug property
 * @returns {Object} Sitemap entry
 */
export const generateCitySitemapEntry = (city) => {
  const lastmod = new Date().toISOString().split('T')[0]
  return {
    loc: getCityUrl(city.Slug),
    lastmod,
    changefreq: 'weekly',
    priority: 0.8
  }
}

/**
 * Generate full sitemap XML
 * @param {Array} cities - Array of city objects
 * @returns {string} Sitemap XML string
 */
export const generateSitemap = (cities) => {
  const lastmod = new Date().toISOString().split('T')[0]
  const entries = [
    {
      loc: siteConfig.baseUrl,
      lastmod,
      changefreq: 'weekly',
      priority: 1.0
    },
    ...cities.map(generateCitySitemapEntry)
  ]
  
  const urlEntries = entries.map(entry => `  <url>
    <loc>${entry.loc}</loc>
    <lastmod>${entry.lastmod}</lastmod>
    <changefreq>${entry.changefreq}</changefreq>
    <priority>${entry.priority}</priority>
  </url>`).join('\n')
  
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`
}

